import {
  format,
  isSameYear,
  isThisYear,
  isToday,
  isYesterday,
  fromUnixTime,
  differenceInDays,
} from 'date-fns';
import { ref } from 'vue';
import { enUS, es, ptBR } from 'date-fns/locale';

const DATE_FNS_LOCALES = {
  en: enUS,
  es,
  pt: ptBR,
};

const detectRuntimeLocale = () =>
  globalThis?.window?.chatwootConfig?.selectedLocale ||
  globalThis?.window?.$chatwoot?.locale ||
  globalThis?.document?.documentElement?.lang ||
  'es';

const normalizeLocale = locale =>
  `${locale || 'es'}`.toLowerCase().replace('-', '_');

const runtimeLocale = ref(normalizeLocale(detectRuntimeLocale()));

export const setRuntimeLocale = locale => {
  runtimeLocale.value = normalizeLocale(locale || detectRuntimeLocale());
};

const getRuntimeLocale = () => {
  if (!runtimeLocale.value) {
    setRuntimeLocale(detectRuntimeLocale());
  }

  return runtimeLocale.value;
};

export const getIntlLocale = localeCode =>
  normalizeLocale(localeCode || getRuntimeLocale()).replace(/_/g, '-');

export const getDateFnsLocale = localeCode => {
  const localeKey = normalizeLocale(localeCode || getRuntimeLocale()).split(
    '_'
  )[0];
  return DATE_FNS_LOCALES[localeKey] || es;
};

const isSpanishLocale = localeCode =>
  normalizeLocale(localeCode || getRuntimeLocale()).startsWith('es');
const isPortugueseLocale = localeCode =>
  normalizeLocale(localeCode || getRuntimeLocale()).startsWith('pt');

const hasLocalizedDateTokens = pattern => /(LLL|MMM|MMMM|EEE|EEEE|a|p)/.test(pattern);

const buildIntlFormatOptions = pattern => {
  const options = {};

  if (pattern.includes('EEEE')) {
    options.weekday = 'long';
  } else if (pattern.includes('EEE')) {
    options.weekday = 'short';
  }

  if (pattern.includes('MMMM')) {
    options.month = 'long';
  } else if (/(LLL|MMM)/.test(pattern)) {
    options.month = 'short';
  }

  if (pattern.includes('dd')) {
    options.day = '2-digit';
  } else if (pattern.includes('d')) {
    options.day = 'numeric';
  }

  if (/(yyyy|yy|\by\b)/.test(pattern)) {
    options.year = 'numeric';
  }

  if (pattern.includes('hh:mm')) {
    options.hour = '2-digit';
    options.minute = '2-digit';
    options.hour12 = true;
  } else if (pattern.includes('h:mm') || pattern.includes('p')) {
    options.hour = 'numeric';
    options.minute = '2-digit';
    options.hour12 = true;
  }

  return options;
};

const formatWithIntl = (date, pattern, localeCode) =>
  new Intl.DateTimeFormat(
    getIntlLocale(localeCode),
    buildIntlFormatOptions(pattern)
  ).format(date);

const formatWithRuntimeLocale = (date, dateFormat, localeCode) =>
  hasLocalizedDateTokens(dateFormat)
    ? formatWithIntl(date, dateFormat, localeCode)
    : format(date, dateFormat, { locale: getDateFnsLocale(localeCode) });

const parseDateInput = time => {
  if (time === null || time === undefined || time === '') return null;

  if (time instanceof Date) {
    return Number.isNaN(time.getTime()) ? null : time;
  }

  if (typeof time === 'number') {
    return time > 9999999999 ? new Date(time) : fromUnixTime(time);
  }

  const parsedDate = new Date(time);
  return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
};

const normalizeRelativeTime = relativeTime => {
  return relativeTime
    .replace(/\babout\b\s*/gi, '')
    .replace(/\baround\b\s*/gi, '')
    .replace(/\balrededor de\b\s*/gi, '')
    .replace(/\s+/g, ' ')
    .trim();
};

const getNowLabel = localeCode => {
  if (isSpanishLocale(localeCode)) return 'ahora';
  if (isPortugueseLocale(localeCode)) return 'agora';
  return 'now';
};

const getRelativeTimeParts = date => {
  const diffInMs = date.getTime() - Date.now();
  const absDiffInMs = Math.abs(diffInMs);
  const minuteInMs = 60 * 1000;
  const hourInMs = 60 * minuteInMs;
  const dayInMs = 24 * hourInMs;
  const monthInMs = 30 * dayInMs;
  const yearInMs = 365 * dayInMs;

  if (absDiffInMs < minuteInMs) return null;
  if (absDiffInMs < hourInMs) {
    return { value: Math.round(diffInMs / minuteInMs), unit: 'minute' };
  }
  if (absDiffInMs < dayInMs) {
    return { value: Math.round(diffInMs / hourInMs), unit: 'hour' };
  }
  if (absDiffInMs < monthInMs) {
    return { value: Math.round(diffInMs / dayInMs), unit: 'day' };
  }
  if (absDiffInMs < yearInMs) {
    return { value: Math.round(diffInMs / monthInMs), unit: 'month' };
  }

  return { value: Math.round(diffInMs / yearInMs), unit: 'year' };
};

/**
 * Formats a Unix timestamp into a human-readable time format.
 * @param {number} time - Unix timestamp.
 * @param {string} [dateFormat='h:mm a'] - Desired format of the time.
 * @returns {string} Formatted time string.
 */
export const messageStamp = (time, dateFormat = 'h:mm a', localeCode) => {
  const unixTime = fromUnixTime(time);
  return formatWithRuntimeLocale(unixTime, dateFormat, localeCode);
};

/**
 * Provides a formatted timestamp, adjusting the format based on the current year.
 * @param {number} time - Unix timestamp.
 * @param {string} [dateFormat='MMM d, yyyy'] - Desired date format.
 * @returns {string} Formatted date string.
 */
export const messageTimestamp = (time, dateFormat = 'MMM d, yyyy', localeCode) => {
  const messageTime = fromUnixTime(time);
  const now = new Date();
  const messageDate = formatWithRuntimeLocale(
    messageTime,
    dateFormat,
    localeCode
  );
  if (!isSameYear(messageTime, now)) {
    return formatWithRuntimeLocale(messageTime, 'LLL d y, h:mm a', localeCode);
  }
  return messageDate;
};

/**
 * Formats a Unix timestamp relative to today: the time for today, a caller-
 * supplied label for yesterday, and a date otherwise. The yesterday label is
 * passed in so the caller keeps ownership of translation.
 * @param {number} time - Unix timestamp.
 * @param {string} yesterdayLabel - Localized label shown for yesterday.
 * @returns {string} Formatted timestamp string.
 */
export const relativeDayTimestamp = (time, yesterdayLabel) => {
  const date = fromUnixTime(time);
  if (isToday(date)) return format(date, 'h:mm a');
  if (isYesterday(date)) return yesterdayLabel;
  if (isThisYear(date)) return format(date, 'MMM d');
  return format(date, 'MMM d, yyyy');
};

/**
 * Converts a date-like input to a relative time string (e.g., 3 hours ago).
 * @param {number|string|Date} time - Unix timestamp (seconds), ISO date string, or Date.
 * @returns {string} Relative time string.
 */
export const dynamicTime = (time, localeCode) => {
  const parsedTime = parseDateInput(time);
  if (!parsedTime) return '';

  const relativeTimeParts = getRelativeTimeParts(parsedTime);
  if (!relativeTimeParts) {
    return getNowLabel(localeCode);
  }

  const relativeTime = new Intl.RelativeTimeFormat(getIntlLocale(localeCode), {
    numeric: 'always',
  }).format(relativeTimeParts.value, relativeTimeParts.unit);

  return normalizeRelativeTime(relativeTime);
};

/**
 * Formats a Unix timestamp into a specified date format.
 * @param {number} time - Unix timestamp.
 * @param {string} [dateFormat='MMM d, yyyy'] - Desired date format.
 * @returns {string} Formatted date string.
 */
export const dateFormat = (time, df = 'MMM d, yyyy', localeCode) => {
  const unixTime = fromUnixTime(time);
  return formatWithRuntimeLocale(unixTime, df, localeCode);
};

/**
 * Converts a detailed time description into a shorter format, optionally appending 'ago'.
 * @param {string} time - Detailed time description (e.g., 'a minute ago').
 * @param {boolean} [withAgo=false] - Whether to append 'ago' to the result.
 * @returns {string} Shortened time description.
 */
export const shortTimestamp = (time, withAgo = false, localeCode) => {
  if (!time) return '';

  // This function takes a time string and converts it to a short time string
  // with the following format: 1m, 1h, 1d, 1mo, 1y
  // The function also takes an optional boolean parameter withAgo
  // which will add the word "ago" to the end of the time string
  const affixRelativeTime = value => {
    if (!withAgo) return value;
    return isSpanishLocale(localeCode) ? `hace ${value}` : `${value} ago`;
  };

  const normalizedTime = time
    .toString()
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(
      /\b(about|over|almost|around|alrededor de|hace|ago|in|en|em|dentro de|daqui a|ha)\b/g,
      ''
    )
    .replace(/\s+/g, ' ')
    .trim();

  if (
    normalizedTime === 'now' ||
    normalizedTime === 'ahora' ||
    normalizedTime === 'agora' ||
    normalizedTime.includes('less than a minute') ||
    normalizedTime.includes('menos de un minuto') ||
    normalizedTime.includes('menos de um minuto')
  ) {
    return getNowLabel(localeCode);
  }

  const singularMappings = {
    'a minute': '1m',
    'an hour': '1h',
    'a day': '1d',
    'a month': '1mo',
    'a year': '1y',
    'un minuto': '1m',
    'una hora': '1h',
    'un dia': '1d',
    'un mes': '1mo',
    'un ano': '1y',
  };

  if (singularMappings[normalizedTime]) {
    return affixRelativeTime(singularMappings[normalizedTime]);
  }

  const patterns = [
    { regex: /(\d+)\s+(?:minute|minutes|minuto|minutos)\b/, unit: 'm' },
    { regex: /(\d+)\s+(?:hour|hours|hora|horas)\b/, unit: 'h' },
    { regex: /(\d+)\s+(?:day|days|dia|dias)\b/, unit: 'd' },
    { regex: /(\d+)\s+(?:month|months|mes|meses)\b/, unit: 'mo' },
    { regex: /(\d+)\s+(?:year|years|ano|anos)\b/, unit: 'y' },
  ];

  for (const { regex, unit } of patterns) {
    const matches = normalizedTime.match(regex);
    if (matches) {
      return affixRelativeTime(`${matches[1]}${unit}`);
    }
  }

  return time;
};

/**
 * Formats a duration in seconds into mm:ss or hh:mm:ss.
 * @param {number|string} durationInSeconds - Duration in seconds.
 * @returns {string} Formatted duration string. Empty string for invalid input.
 */
export const formatDuration = durationInSeconds => {
  if (durationInSeconds === null || durationInSeconds === undefined) return '';

  const totalSeconds = Number(durationInSeconds);
  if (Number.isNaN(totalSeconds) || totalSeconds < 0) return '';

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const mm = minutes.toString().padStart(2, '0');
  const ss = seconds.toString().padStart(2, '0');
  if (hours > 0) {
    return `${hours.toString().padStart(2, '0')}:${mm}:${ss}`;
  }
  return `${mm}:${ss}`;
};

/**
 * Calculates the difference in days between now and a given timestamp.
 * @param {Date} now - Current date/time.
 * @param {number} timestampInSeconds - Unix timestamp in seconds.
 * @returns {number} Number of days difference.
 */
export const getDayDifferenceFromNow = (now, timestampInSeconds) => {
  const date = new Date(timestampInSeconds * 1000);
  return differenceInDays(now, date);
};

/**
 * Checks if more than 24 hours have passed since a given timestamp.
 * Useful for determining if retry/refresh actions should be disabled.
 * @param {number} timestamp - Unix timestamp.
 * @returns {boolean} True if more than 24 hours have passed.
 */
export const hasOneDayPassed = timestamp => {
  if (!timestamp) return true; // Defensive check
  return getDayDifferenceFromNow(new Date(), timestamp) >= 1;
};
